import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { UserPlus, Trash2, Crown, Loader2, FileText, Flag, ArrowDownCircle, ArrowUpCircle, Wallet, Star } from "lucide-react";

const initials = (n) => (n || "?").split(" ").map((x) => x[0]).slice(0, 2).join("");

const LEVELS = [
  { v: "none", label: "—", title: "Sin acceso" },
  { v: "view", label: "Ver", title: "Solo ver" },
  { v: "edit", label: "Editar", title: "Ver y editar" },
];
const LORDER = { none: 0, view: 1, edit: 2 };
const SECTIONS = [
  { key: "budget", label: "Presupuesto", icon: FileText },
  { key: "ingresos", label: "Ingresos", icon: ArrowDownCircle },
  { key: "gastos", label: "Gastos", icon: ArrowUpCircle },
];

function LevelControl({ section, uid, current, max, canManage, onChange }) {
  return (
    <div className="inline-flex rounded-md border border-border overflow-hidden bg-white" data-testid={`member-${section}-${uid}`}>
      {LEVELS.map((lv) => {
        const active = (current || "none") === lv.v;
        const allowed = LORDER[lv.v] <= LORDER[max || "none"];
        const disabled = !canManage || !allowed;
        return (
          <button
            key={lv.v} type="button" title={lv.title} disabled={disabled && !active}
            onClick={() => { if (!active && !disabled) onChange(lv.v); }}
            data-testid={`member-${section}-${lv.v}-${uid}`}
            className={`px-2.5 py-1 text-xs font-medium transition-colors duration-150 border-l first:border-l-0 border-border ${active ? "bg-primary text-primary-foreground" : "text-muted-foreground"} ${disabled ? (active ? "" : "opacity-40 cursor-not-allowed") : "hover:bg-muted cursor-pointer"}`}
          >
            {lv.label}
          </button>
        );
      })}
    </div>
  );
}

export default function ProjectMembers({ projectId, onChanged }) {
  const [data, setData] = useState(null);
  const [team, setTeam] = useState([]);
  const [adding, setAdding] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [m, t] = await Promise.all([api.get(`/projects/${projectId}/members`), api.get("/team")]);
      setData(m.data); setTeam(t.data || []);
    } catch (e) { toast.error("No se pudieron cargar los miembros"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);

  const refresh = (members) => { setData((d) => ({ ...d, members })); onChanged?.(); };

  const addMember = async () => {
    if (!adding) return;
    setBusy(true);
    try { const r = await api.post(`/projects/${projectId}/members`, { user_id: adding }); refresh(r.data.members); setAdding(""); toast.success("Miembro añadido"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setBusy(false); }
  };
  const removeMember = async (uid) => {
    if (!window.confirm("¿Quitar a esta persona del proyecto?")) return;
    try { const r = await api.delete(`/projects/${projectId}/members/${uid}`); refresh(r.data.members); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const setPerm = async (uid, patch) => {
    try { const r = await api.patch(`/projects/${projectId}/members/${uid}`, patch); refresh(r.data.members); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  if (!data) return <div className="flex justify-center py-14"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  const grant = data.can_grant || {};
  const memberIds = new Set((data.members || []).map((m) => m.user_id));
  const available = team.filter((u) => !memberIds.has(u.user_id));

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-heading font-bold text-lg">Equipo del proyecto</h3>
          <p className="text-sm text-muted-foreground max-w-xl">
            Solo los miembros (además de Dirección y Administración) pueden acceder a este proyecto.
            Marca <span className="font-medium">Responsable</span> para que uno o varios puedan cerrar/eliminar el proyecto y cerrar hitos.
            Para cada sección elige el nivel: <span className="font-medium">Sin acceso (—)</span>, <span className="font-medium">Ver</span> o <span className="font-medium">Editar</span>.
          </p>
        </div>
        {data.can_manage && (
          <div className="flex items-center gap-2">
            <Select value={adding} onValueChange={setAdding}>
              <SelectTrigger className="w-[220px]" data-testid="member-add-select"><SelectValue placeholder="Añadir persona…" /></SelectTrigger>
              <SelectContent>
                {available.length === 0 && <div className="px-2 py-1.5 text-sm text-muted-foreground">Todos añadidos</div>}
                {available.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name} · {u.role}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button onClick={addMember} disabled={!adding || busy} className="gap-1.5" data-testid="member-add-btn"><UserPlus className="h-4 w-4" /> Añadir</Button>
          </div>
        )}
      </div>

      <Card className="border-border shadow-none divide-y divide-border overflow-hidden">
        {(data.members || []).length === 0 && <div className="py-10 text-center text-sm text-muted-foreground">Sin miembros todavía.</div>}
        {(data.members || []).map((m) => (
          <div key={m.user_id} className="flex flex-col lg:flex-row lg:items-center gap-3 p-3.5" data-testid={`member-row-${m.user_id}`}>
            <div className="flex items-center gap-3 min-w-0 lg:w-64 shrink-0">
              <Avatar className="h-9 w-9 shrink-0"><AvatarImage src={m.picture} /><AvatarFallback className="text-[11px] bg-muted">{initials(m.name)}</AvatarFallback></Avatar>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="font-medium text-sm truncate max-w-[150px]">{m.name}</span>
                  {m.is_creator && <Crown className="h-3 w-3 text-gold shrink-0" title="Creador" />}
                  {m.is_lead && <Badge variant="outline" className="border-transparent bg-primary/12 text-primary text-[10px] px-1.5 py-0 shrink-0 gap-0.5" data-testid={`member-lead-badge-${m.user_id}`}><Star className="h-2.5 w-2.5" /> Responsable</Badge>}
                  <Badge variant="outline" className="border-transparent bg-muted text-muted-foreground text-[10px] px-1.5 py-0 shrink-0">{m.role}</Badge>
                </div>
                <span className="text-xs text-muted-foreground truncate block">{m.email}</span>
              </div>
            </div>

            <div className="flex items-end gap-4 lg:gap-6 flex-1 flex-wrap pl-12 lg:pl-2">
              {SECTIONS.map((s) => (
                <div key={s.key} className="flex flex-col gap-1">
                  <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground"><s.icon className="h-3 w-3" /> {s.label}</span>
                  <LevelControl section={s.key} uid={m.user_id} current={m.levels?.[s.key]} max={grant[s.key]} canManage={data.can_manage} onChange={(lvl) => setPerm(m.user_id, { [s.key]: lvl })} />
                </div>
              ))}
              <div className="flex flex-col gap-1">
                <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground"><Flag className="h-3 w-3" /> Cierre</span>
                <div className="inline-flex items-center gap-2 h-[28px]">
                  <Switch checked={!!m.cierre_view} disabled={!data.can_manage || !grant.cierre} onCheckedChange={(v) => setPerm(m.user_id, { cierre: v })} data-testid={`member-cierre-${m.user_id}`} />
                  <span className="text-xs text-muted-foreground">{m.cierre_view ? "Ver" : "—"}</span>
                </div>
              </div>
              <div className="flex flex-col gap-1">
                <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground"><Wallet className="h-3 w-3" /> Pagos externos</span>
                <div className="inline-flex items-center gap-2 h-[28px]">
                  <Switch checked={!!m.extpay_view} disabled={!data.can_manage || !grant.extpay} onCheckedChange={(v) => setPerm(m.user_id, { extpay: v })} data-testid={`member-extpay-${m.user_id}`} />
                  <span className="text-xs text-muted-foreground">{m.extpay_view ? "Ver" : "—"}</span>
                </div>
              </div>
              <div className="flex flex-col gap-1">
                <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground"><Star className="h-3 w-3" /> Responsable</span>
                <div className="inline-flex items-center gap-2 h-[28px]">
                  <Switch checked={!!m.is_lead} disabled={!data.can_manage || !grant.lead} onCheckedChange={(v) => setPerm(m.user_id, { is_lead: v })} data-testid={`member-lead-${m.user_id}`} />
                  <span className="text-xs text-muted-foreground">{m.is_lead ? "Sí" : "—"}</span>
                </div>
              </div>
            </div>

            {data.can_manage && !m.is_creator && (
              <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground hover:text-destructive shrink-0 self-end lg:self-center" onClick={() => removeMember(m.user_id)} data-testid={`member-remove-${m.user_id}`}><Trash2 className="h-4 w-4" /></Button>
            )}
          </div>
        ))}
      </Card>
    </div>
  );
}
